import cx from 'classnames';
import {ChangeEvent, Dispatch, FC, SetStateAction, useCallback} from 'react';
import {Input} from '~library/atoms/Input';
import {Typography} from '~library/atoms/Typography';

interface GuestOptionMaxGuestsProps {
  checked: boolean;
  disabled: boolean;
  dataQaId?: string;
  maxGuestList: number | null;
  setMaxGuestList: (value: number | null) => void;
  showMaxGuest: boolean;
  setShowMaxGuest: Dispatch<SetStateAction<boolean>>;
}

export const GuestOptionMaxGuests: FC<GuestOptionMaxGuestsProps> = ({
  checked,
  disabled,
  dataQaId,
  maxGuestList,
  setMaxGuestList,
  showMaxGuest,
  setShowMaxGuest,
}) => {
  const maxGuestListSize = 5000;

  const handleToggleChange = useCallback(() => {
    setShowMaxGuest((prev: boolean) => !prev);
    if (showMaxGuest) {
      setMaxGuestList(0);
    }
  }, [showMaxGuest, setShowMaxGuest, setMaxGuestList]);

  const valueToShow = useCallback(() => {
    return maxGuestList && maxGuestList > 0 ? parseInt(maxGuestList.toString(), 10) : '';
  }, [maxGuestList]);

  return (
    <div className="edit-flow__guest-options__option">
      <div className="edit-flow__guest-options__title">
        <Typography variant="paragraph2" color={disabled ? 'coin' : 'charcoal'}>
          Set an event capacity
        </Typography>
        <Typography variant="paragraph3" color={disabled ? 'coin' : 'pebble'}>
          Limit the total number of guests and plus ones who can RSVP "Yes" to your event.
        </Typography>
        <Typography variant="paragraph4" color={disabled ? 'coin' : 'pebble'}>
          <i>New guests won't be able to RSVP once capacity is reached. Limit includes host.</i>
        </Typography>
        <label className={cx('input-toggle-container hidden')}>
          <input
            type="checkbox"
            checked={checked}
            onChange={handleToggleChange}
            disabled={disabled}
          />
          <span
            className={cx('input-toggle hidden', {disabled})}
            data-checked={checked}
            data-qa-id={dataQaId}
          />
        </label>
      </div>
      <div className="edit-flow__guest-options__capacity-input">
        <Input
          allowEmpty
          disabled={disabled}
          type="number"
          min={1}
          max={maxGuestListSize}
          value={valueToShow() ?? ''}
          data-qa-value={parseInt(maxGuestList?.toString() || '0', 10)}
          placeholder="None"
          data-qa-id="event_option_max_guests"
          error={
            maxGuestList && maxGuestList > maxGuestListSize ? `Maximum ${maxGuestListSize}` : ''
          }
          id="event_option_max_guests"
          onKeyPress={(e) => {
            if (!/^\d$/.test(e.key)) e.preventDefault();
          }}
          onChange={(e: ChangeEvent<HTMLInputElement>) => {
            const val = e.target.value;
            if (parseInt(val, 10) < 1 || val === '') {
              setMaxGuestList(null);
            } else {
              setMaxGuestList(parseInt(val, 10));
            }
          }}
        />
      </div>
    </div>
  );
};
